import React from "react";
import { Link } from "react-scroll";
import { Link as RouterLink } from "react-router-dom";
import LocalizedLink from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import logo from "../../images/logo.jpg";
import ThemeToggle from "@/components/ThemeToggle/ThemeToggle";
import LanguageToggle from "@/components/LanguageToggle/LanguageToggle";
import { trackEvent } from "@/utils/analytics";
import NewsletterSignup from "@/components/NewsletterSignup";
import "./Footer.scss";

const scrollOptions = {
  duration: 500,
  smooth: true,
  offset: -80,
};

const Footer: React.FC = () => {
  const { t } = useTranslation();

  const footerLinks = [
    { to: "capabilities-section", key: "solutions", label: t("footer.solutions") },
    { to: "about-section", key: "about", label: t("footer.about") },
    { to: "portfolio-section", key: "work", label: t("footer.work") },
    { to: "contact-section", key: "contact", label: t("footer.contact") },
  ];

  return (
    <footer className="footer">
      <div className="footer__inner">
        <div className="footer__top">
          <div className="footer__brand">
            <div className="footer__logo">
              <img src={logo} alt="Devbloom" className="footer__logo-icon" />
              <span className="footer__logo-text">Devbloom</span>
            </div>
            <p className="footer__tagline">{t("footer.tagline")}</p>
          </div>

          <nav className="footer__links">
            {footerLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                {...scrollOptions}
                className="footer__link"
                onClick={() => trackEvent("cta_click", { location: `footer_${link.key}` })}
              >
                {link.label}
              </Link>
            ))}
            <LocalizedLink
              to="/insights"
              className="footer__link"
              onClick={() => trackEvent("cta_click", { location: "footer_insights" })}
            >
              {t("insights.label")}
            </LocalizedLink>
            <RouterLink
              to="/faq"
              className="footer__link"
              onClick={() => trackEvent("cta_click", { location: "footer_faq" })}
            >
              {t("faq.heading")}
            </RouterLink>
            <RouterLink
              to="/built-by-agents"
              className="footer__link"
              onClick={() => trackEvent("cta_click", { location: "footer_built_by_agents" })}
            >
              {t("footer.builtByAgents")}
            </RouterLink>
          </nav>
        </div>

        <div className="footer__newsletter">
          <NewsletterSignup variant="inline" />
        </div>

        <div className="footer__bottom">
          <span className="footer__copyright">
            &copy; {new Date().getFullYear()} {t("footer.copyright")}
          </span>
          <div className="footer__controls">
            <LanguageToggle />
            <ThemeToggle />
            <a
              href="https://www.linkedin.com/company/devbloom/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer__social"
              aria-label="LinkedIn"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
